import React, { useState, useRef, useEffect, useCallback } from "react";
import { ImageEditorModalProps } from "../../types/modal";
import defaultImage from "../../assets/images/default-img.jpg";
import fileApi from "../../api/fileApi";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../utils/crop-image";
import smartcrop from "smartcrop";

type CroppedAreaPixels = { width: number; height: number; x: number; y: number };
export default function ImageEditorModal(
  props: Readonly<ImageEditorModalProps>
) {
  // Initialize imagePreview with the existing image URL from props if available
  const [imagePreview, setImagePreview] = useState<string | null>(
    props.existingImageUrl || null
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Cropping state
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);
  const [tempImage, setTempImage] = useState<string | null>(null);

  // NEW: store SmartCrop's suggested crop to initialize the cropper
  const [initialCroppedAreaPixels, setInitialCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isLoading) {
        props.onClose();
      }
    },
    [props, isLoading]
  );

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setTempImage(previewURL);

      // Run SmartCrop to get best crop area before enabling crop mode
      const img = new Image();
      img.src = previewURL;
      img.onload = async () => {
        try {
          const targetWidth = props.width || 200;
          const targetHeight = props.height || 200;

          const result = await smartcrop.crop(img, {
            width: targetWidth,
            height: targetHeight,
          });

          if (result && result.topCrop) {
            const c = result.topCrop; // { x, y, width, height } in original image pixels
            setInitialCroppedAreaPixels({
              x: c.x,
              y: c.y,
              width: c.width,
              height: c.height,
            });
          } else {
            setInitialCroppedAreaPixels(null);
          }
        } catch (err) {
          console.error("SmartCrop failed:", err);
          setInitialCroppedAreaPixels(null);
        } finally {
          setCropMode(true);
        }
      };
      img.onerror = () => {
        console.error("Failed to load image for SmartCrop");
        setInitialCroppedAreaPixels(null);
        setCropMode(true);
      };
    }
  };

  const onCropComplete = useCallback(
    (_croppedArea: { x: number; y: number; width: number; height: number }, croppedPixels: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedPixels);
    },
    []
  );

  const confirmCrop = async () => {
    if (!tempImage || !croppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedImg(tempImage, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "cropped.jpg", { type: "image/jpeg" });
      setImageFile(croppedFile);
      setImagePreview(URL.createObjectURL(croppedFile));
      setCropMode(false);
    } catch (error) {
      console.error("Crop failed:", error);
    }
  };

  const cancelCrop = () => {
    setTempImage(null);
    setCropMode(false);
    setInitialCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileRemoval = async () => {
    try {
      // TODO: call BE with image node data to remove the file from storage & update the DB record
      setImagePreview(null);
      setImageFile(null);

      // Reset the file input field
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      props.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (props.open) {
      // Show existing image if available, otherwise null
      setImagePreview(props.existingImageUrl || null);
      setImageFile(null);
      document.body.style.overflow = 'hidden';

      // Reset the file input field
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Reset SmartCrop init state
      setInitialCroppedAreaPixels(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setTempImage(null);
      setCropMode(false);
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [props.open, props.existingImageUrl]);

  const handleSubmit = async () => {
    if (!imageFile || !props.image_node_id) return;

    const formData = new FormData();
    formData.append("file", imageFile);

    // append file identifier properties as text fields
    formData.append("design_id", props.design_id);
    formData.append("frame_id", props.frame_id);
    formData.append("image_node_id", props.image_node_id);

    try {
      setIsLoading(true);
      const response = await fileApi.uploadFile(formData);

      if (response.data.statusCode !== 200) {
        console.error(response.statusText);
        throw new Error(response.statusText);
      }

      // send back the object URL from S3
      const updatedUrl = `${response.data.data}?t=${Date.now()}`;
      props.handleSave(updatedUrl);
      setImagePreview(updatedUrl);
      props.onClose(); // Close modal after successful upload
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
      // Reset the preview and file input
      setImagePreview(null);
      setImageFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      // Call parent's handleReset
      if (props.image_node_id) {
        props.handleReset();
      }
      props.onClose();
    };

  if (!props.open) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content">
        {cropMode ? (
          <>
            <div className="modal-header">
              <h2 className="modal-title">Crop Image</h2>
            </div>
            <div className="modal-body" style={{ position: "relative", height: "400px" }}>
              <Cropper
                key={(tempImage || "") + "_" + (initialCroppedAreaPixels ? `${initialCroppedAreaPixels.x}-${initialCroppedAreaPixels.y}-${initialCroppedAreaPixels.width}-${initialCroppedAreaPixels.height}` : "noinit")}
                image={tempImage || ""}
                crop={crop}
                zoom={zoom}
                aspect={props.width && props.height ? props.width / props.height : 1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={false}
                initialCroppedAreaPixels={initialCroppedAreaPixels || undefined}
              />
            </div>
            <div className="modal-footer">
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelCrop}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmCrop}
              >
                Apply Crop
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-header">
              <h2 className="modal-title">
                Update Image
              </h2>
            </div>
            <div className="modal-body">
              <div className="form-wrapper">
                <div className="image-info mb-4">
                  <div className="dimensions-display">
                    <span className="dimension-item">
                      <strong>Width:</strong> {props.width}px
                    </span>
                    <span className="dimension-separator">|</span>
                    <span className="dimension-item">
                      <strong>Height:</strong> {props.height}px
                    </span>
                  </div>
                </div>
                
                <div className="input-wrapper">
                  <label htmlFor="image-file" className="form-label text-start">
                    Select Image File
                  </label>
                  <input
                    ref={fileInputRef}
                    id="image-file"
                    className="form-control"
                    type="file"
                    accept=".png,.jpg,.jpeg,.gif"
                    onChange={handleFileSelection}
                    disabled={isLoading}
                  />
                  <div className="file-help-text">
                    Accepted formats: PNG, JPG, JPEG, GIF
                  </div>
                </div>
                
                <div className="image-preview-section">
                  <label className="form-label text-start">
                    Image Preview
                  </label>
                  <div className="image-preview-container">
                    <img
                      src={imagePreview ?? defaultImage}
                      className="image-preview"
                      alt="Image Preview"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleFileRemoval}
                disabled={isLoading}
              >
                Cancel
              </button>
              {imagePreview && (
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleReset}
                  disabled={isLoading}
                >
                  Reset
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={isLoading || !imageFile}
                onClick={handleSubmit}
              >
                {isLoading ? "Uploading..." : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
